import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { affiliateApis } from "@services/apis/Affiliate";
import {
  GetMerchantCommisionsData,
  GetMerchantCommisionsResponse,
} from "@services/models/Affiliate/GetMerchantCommisionsModel";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface MerchantCommisionsSliderProps {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  merchantName?: string;
}

const MerchantCommisionsSlider: React.FC<MerchantCommisionsSliderProps> = ({
  isOpen,
  onClose,
  id,
  merchantName = "Merchant",
}) => {
  const [loading, setLoading] = useState(false);
  const [merchantCommision, setMerchantCommision] = useState<
    GetMerchantCommisionsData[]
  >([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const fetchMerchantCommisions = async () => {
    try {
      setLoading(true);
      setMerchantCommision([]);
      const response: GetMerchantCommisionsResponse =
        await affiliateApis.getMerchantCommisions({
          id: id,
        });
      setMerchantCommision(response.data);
    } catch (err: any) {
      setMerchantCommision([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionChange = (merchantId: number, value: string) => {
    setMerchantCommision((prev) =>
      prev.map((commission) =>
        commission.merchantId === merchantId
          ? { ...commission, commission: value }
          : commission
      )
    );
  };

  const handleUpdateCommissions = async () => {
    try {
      setUpdateLoading(true);
      const updateData = merchantCommision.map((commission) => ({
        merchantId: commission.merchantId,
        commission: commission.commission,
      }));

      await affiliateApis.updateMerchantCommision(updateData);
      handleSuccess({ message: "Commissions updated successfully" });
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Failed to update commissions. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMerchantCommisions();
    }
  }, [isOpen, id]);

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClose={onClose}
      heading={`${merchantName} Edit Rates`}
      showConfirm={false}
      widthDesktop={"35%"}
      isSlider={true}
      onClickCross={onClose}
    >
      <div className="flex flex-col mt-[54px] overflow-hidden">
        {loading ? (
          <div className="bg-bg-base p-[16px] border border-border-subtle mt-[34px] mx-4 rounded-lg">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="mb-4">
                <Skeleton height={20} className="mb-2" />
                <Skeleton height={40} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4 bg-bg-base p-[16px] border border-border-subtle mt-[34px] mx-4 rounded-lg flex-1 overflow-y-auto">
            {merchantCommision.length > 0 ? (
              <div className="space-y-4">
                {merchantCommision.map((commission) => (
                  <div
                    key={commission.merchantId}
                    className="flex flex-row items-center justify-between"
                  >
                    <p className="text-text-base text-[12px] font-normal min-w-[50%]">
                      {commission.name}
                    </p>
                    <GlobalTextInputField
                      type="number"
                      allowDecimal={true}
                      value={commission.commission}
                      onChange={(e) =>
                        handleCommissionChange(
                          commission.merchantId,
                          e.target.value
                        )
                      }
                      placeholder="Enter commission rate"
                      fontSize="12px"
                      className="w-full"
                      suffixIcon={
                        <span className="text-text-subtle text-[12px]">%</span>
                      }
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-text-subtle text-[12px]">
                No commission data available
              </div>
            )}
            {merchantCommision.length > 0 && (
              <LightCustomButton
                text="UPDATE RATES"
                isActive={true}
                isButtonLoading={updateLoading}
                onClick={handleUpdateCommissions}
                textSize="12px"
                className="w-full"
              />
            )}
          </div>
        )}
      </div>
    </SlidingPanel>
  );
};

export default MerchantCommisionsSlider;
